import { Account } from '@/domain/usecases'
import { i18nTranslationHelper } from '@/i18n'
import { FeatureFlag } from '@/infra/feature-flag/feature-flag'
import { makeSkillWebAuthorizeRedirectUrl } from '@/main/factories/http'
import {
  makeRemoteAccountProfile,
  makeRemoteUserProfileAssessment
} from '@/main/factories/usecases'
import PathConstants from '@/main/routes/path-contants'
import { useLDClient } from 'launchdarkly-react-client-sdk'
import { createContext, Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react'
import { useQueryClient } from 'react-query'
import { getDisplayName, getHelpCenterConfig } from '../../helpers/account-profile-helper'
import { Analytics } from './analytics'

type AccountProviderProps = {
  children: ReactNode
  initialState?: AccountContextValueType
}

export type HelpCenterConfigType = {
  companyId?: string
  redirectTo: string
  hasChatBot: boolean
  hasLink: boolean
  hasWhatsApp: boolean
}

export type AccountWithCustomProperties = Account.Model & {
  displayName: string
  helpCenterConfig: HelpCenterConfigType
}
type AccountContextValueType = AccountWithCustomProperties | null

export type AccountContextType =
  | null
  | [AccountContextValueType, Dispatch<SetStateAction<AccountContextValueType>>]

export type ValidAccountState = [
  AccountWithCustomProperties,
  Dispatch<SetStateAction<AccountWithCustomProperties>>
]

const AccountContext = createContext({} as AccountContextType)

const changeAppLanguageByUserPreference = (language: string) => {
  const currentLanguage = i18nTranslationHelper.getCurrentLanguage()
  const supportedLanguages = i18nTranslationHelper.getAllLanguages()

  if (!supportedLanguages.includes(language)) {
    return
  }

  if (currentLanguage === language) {
    return
  }

  i18nTranslationHelper.changeLanguage(language).then(() => {
    window.location.reload()
  })
}

const AccountProvider = ({ initialState = null, children }: AccountProviderProps) => {
  const [account, setAccount] = useState<AccountContextValueType>(initialState)
  const isAnonnimous = PathConstants.isInStaticRoute()
  const queryClient = useQueryClient()
  const remoteAccountProfile = makeRemoteAccountProfile(queryClient)
  const ldClient = useLDClient()

  const getLatestProfileAssessment = (account: AccountWithCustomProperties) => {
    if (!FeatureFlag.Key.LOAD_PROFILE_ASSESSMENT_ON_START.isEnabled(ldClient)) {
      return
    }
    const remote = makeRemoteUserProfileAssessment(queryClient)
    remote
      .getLatest()
      .then(data => {
        setAccount({
          ...account,
          latestProfileAssessment: data || undefined
        })
      })
      .catch(() => {
        console.warn('Failed to fetch latest profile assessment')
      })
  }

  useEffect(() => {
    if (isAnonnimous) {
      setAccount(Account.getAnonnimous())
    } else if (!account) {
      remoteAccountProfile
        .loaderGet()
        .then(data => {
          const updatedAccount = {
            ...data,
            displayName: getDisplayName(data, ldClient),
            helpCenterConfig: getHelpCenterConfig(data, ldClient)
          }
          setAccount(updatedAccount)
          changeAppLanguageByUserPreference(updatedAccount.preferenceLanguage || 'pt')
          Analytics.trackAccountIdentification(updatedAccount)
          getLatestProfileAssessment(updatedAccount)
        })
        .catch(() => {
          console.warn('Failed to fetch account profile')
          window.location.href = makeSkillWebAuthorizeRedirectUrl()
        })
    }
  }, [])

  return <AccountContext.Provider value={[account, setAccount]}>{children}</AccountContext.Provider>
}

export { AccountContext, AccountProvider }
